import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { AccordionItem, ArrowIcon, EASE_OUT, Reveal, SplitReveal } from "../../components/motion";
import HeroImage from "../../components/HeroImage";
import WorksList from "../../components/WorkList";
import { CONTACT_MAILTO } from "../../components/Header";
import { brands, featuredProjects, ownProducts } from "../../constants/projects";


/* -------------------------------------------------------------------------
   Ana sayfa: Atölye15'in iskeleti (metin hero, güven şeridi, vaka çalışmaları,
   SSS, kapanış CTA) + Neon Apps'in mobil ajans dili (sayılar, hizmet kartları,
   5 adımlı süreç, "ücretsiz görüşme").
   ------------------------------------------------------------------------- */

const ArrowLink = ({ to, href, children, light = false }) => {
    const className = `link-arrow ${light ? "link-arrow-light" : ""}`;
    const inner = (
        <>
            <span className="label">{children}</span>
            <ArrowIcon className="arrow" />
        </>
    );
    if (href) {
        return (
            <a href={href} className={className} target={href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer">
                {inner}
            </a>
        );
    }
    return <Link to={to} className={className}>{inner}</Link>;
};

/* ------------------------------- Hero ----------------------------------- */
const Hero = () => (
    <section className="container-site pt-[48px] md:pt-[96px]" aria-labelledby="hero-title">
        <h1 id="hero-title" className="t-display max-w-[1040px]">
            <SplitReveal text="Mobil uygulamanız ve web ürününüz için uzman bir yazılım danışmanı" delay={0.1} />
        </h1>
        <Reveal delay={0.4} className="mt-8 flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
            <p className="t-intro max-w-[640px] font-normal text-ink-600">
                React Native, Flutter ve Node.js ile fikirden mağazaya. Kendi uygulamalarım 135 binden fazla indirmeye ulaştı; müşteri projelerinde yedi yılı aşkın deneyim.
            </p>
            <div className="flex flex-wrap items-center gap-x-8 gap-y-4">
                <a href={CONTACT_MAILTO} className="btn-pill">Ücretsiz görüşme planla</a>
                <ArrowLink to="/projeler">Projelere bak</ArrowLink>
            </div>
        </Reveal>
        <div className="mt-[48px] md:mt-[72px]">
            <HeroImage />
        </div>
    </section>
);

/* --------------------------- Güven şeridi ------------------------------- */
const Brands = () => (
    <section className="container-site pt-[80px] md:pt-[120px]" aria-label="Birlikte çalıştığım markalar">
        <Reveal>
            <p className="t-eyebrow text-center">Birlikte çalıştığım markalar</p>
        </Reveal>
        <Reveal delay={0.1}>
            <ul className="mt-10 grid grid-cols-2 items-center gap-x-8 gap-y-10 sm:grid-cols-3 md:grid-cols-5">
                {brands.map((brand) => (
                    <li key={brand.name} className="flex h-[48px] items-center justify-center">
                        {brand.logo ? (
                            <img
                                src={brand.logo}
                                alt={brand.name}
                                className="max-h-[40px] max-w-[140px] object-contain opacity-60 grayscale transition-all duration-500 hover:opacity-100 hover:grayscale-0"
                                loading="lazy"
                            />
                        ) : (
                            <span className="font-display text-[22px] font-bold tracking-[-0.02em] text-ink-300 transition-colors duration-500 hover:text-ink">
                                {brand.name}
                            </span>
                        )}
                    </li>
                ))}
            </ul>
        </Reveal>
    </section>
);

/* --------------------------- Tanıtım + sayılar -------------------------- */
const STATS = [
    { value: "135K+", label: "indirme, kendi uygulamalarım" },
    { value: "30+", label: "yayınlanan proje" },
    { value: "7+", label: "yıl mobil ve web deneyimi" },
    { value: "10+", label: "birlikte çalışılan marka" },
];

const Intro = () => (
    <section className="container-site pt-[96px] md:pt-[140px]" aria-labelledby="intro-title">
        <h2 id="intro-title" className="t-h2 max-w-[900px]">
            <SplitReveal text="Ankara'dan, dünyanın her yerindeki ekiplerle çalışan deneyimli bir yazılım danışmanıyım." stagger={0.02} />
        </h2>
        <Reveal delay={0.2} className="mt-12 grid grid-cols-2 gap-x-8 gap-y-10 md:grid-cols-4">
            {STATS.map((stat) => (
                <div key={stat.value}>
                    <p className="font-display text-[44px] font-bold leading-none tracking-[-0.04em] text-ink md:text-[64px]">{stat.value}</p>
                    <p className="t-body mt-3 text-ink-600">{stat.label}</p>
                </div>
            ))}
        </Reveal>
        <Reveal delay={0.3} className="mt-12">
            <ArrowLink href={CONTACT_MAILTO}>Birlikte çalışalım</ArrowLink>
        </Reveal>
    </section>
);

/* ------------------------------ Hizmetler ------------------------------- */
const SERVICES = [
    {
        title: "Mobil Uygulama Geliştirme",
        text: "iOS ve Android için React Native ve Flutter uygulamaları. İlk prototipten mağaza yayınına; uygulama içi satın alma, bildirim ve analitik dahil.",
    },
    {
        title: "Web Uygulaması ve Yönetim Paneli",
        text: "Tanıtım siteleri, ürün landing sayfaları ve React ile yönetim panelleri. Hızlı, mobil uyumlu ve sürdürülebilir.",
    },
    {
        title: "Backend ve Yapay Zekâ Entegrasyonu",
        text: "Node.js API'leri, MongoDB, Firebase ve AWS; müzik, ses, görsel ve metin üreten yapay zekâ modellerinin ürüne entegrasyonu.",
    },
    {
        title: "Oyun ve Etkileşimli Deneyimler",
        text: "Unity ile çok oyunculu PC oyunları, oyunlaştırılmış ürün özellikleri ve etkileşimli arayüzler.",
    },
    {
        title: "Teknik Danışmanlık",
        text: "Mevcut uygulamanız için mimari ve kod incelemesi, mağaza stratejisi ve uygulanabilir bir yol haritası.",
    },
];

const Services = () => (
    <section id="hizmetler" className="container-site scroll-mt-[110px] pt-[96px] md:pt-[140px]" aria-labelledby="services-title">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-8">
            <div className="lg:col-span-5">
                <Reveal><p className="t-eyebrow">Hizmetler</p></Reveal>
                <h2 id="services-title" className="t-h2 mt-4">
                    <SplitReveal text="Fikirden mağazaya, tek muhatap" />
                </h2>
                <Reveal delay={0.1} className="mt-6 max-w-[460px]">
                    <p className="t-body text-ink-600">
                        Mobil benim başlangıç noktam. Web, oyun ve arka uç, ürün ihtiyaç duyduğunda aynı elden gelir.
                    </p>
                </Reveal>
                <Reveal delay={0.2} className="mt-8">
                    <a href={CONTACT_MAILTO} className="btn-pill">Birlikte çalışalım</a>
                </Reveal>
            </div>
            <div className="lg:col-span-7">
                <Reveal delay={0.2}>
                    {SERVICES.map((service) => (
                        <AccordionItem key={service.title} title={service.title} titleClassName="t-intro">
                            {service.text}
                        </AccordionItem>
                    ))}
                </Reveal>
            </div>
        </div>
    </section>
);

/* ------------------------- Öne çıkan projeler --------------------------- */
const Featured = () => (
    <section id="projeler" className="container-site scroll-mt-[110px] pt-[96px] md:pt-[140px]" aria-labelledby="featured-title">
        <Reveal><p className="t-eyebrow">Öne çıkan projeler</p></Reveal>
        <h2 id="featured-title" className="t-h2 mt-4 max-w-[800px]">
            <SplitReveal text="Yayınlanan ve kullanılan ürünlerden bir seçki" />
        </h2>
        <div className="mt-16 md:mt-24">
            <WorksList items={featuredProjects} />
        </div>
        <Reveal className="mt-[80px] flex justify-center md:mt-[140px]">
            <ArrowLink to="/projeler">Tüm projeler</ArrowLink>
        </Reveal>
    </section>
);

/* --------------------------- Kendi ürünlerim ---------------------------- */
const Products = () => (
    <section id="uygulamalar" className="container-site scroll-mt-[110px] pt-[96px] md:pt-[140px]" aria-labelledby="products-title">
        <div className="bg-ink-900 px-6 py-14 text-white md:px-16 md:py-20">
            <Reveal><p className="t-eyebrow text-ink-400">Kendi ürünlerim</p></Reveal>
            <h2 id="products-title" className="t-display mt-4 max-w-[900px] text-white">
                <SplitReveal text="Müzik, ses ve tasarım üreten yapay zekâ uygulamaları, 135K+ indirme" />
            </h2>
            <ul className="mt-12 grid grid-cols-1 gap-x-12 gap-y-8 md:mt-20 md:grid-cols-2 lg:grid-cols-3">
                {ownProducts.map((product) => (
                    <li key={product.slug} className="border-t border-ink-800 pt-5">
                        <Link to={product.landing || `/projeler/${product.slug}`} className="group block">
                            <h3 className="t-h3 text-white transition-colors duration-300 group-hover:text-ink-300">{product.title}</h3>
                            <p className="t-body mt-1 text-ink-400">{product.blurb}</p>
                        </Link>
                        <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2">
                            {product.links.map((link) => (
                                <ArrowLink key={link.href} href={link.href} light>{link.label}</ArrowLink>
                            ))}
                            <ArrowLink to={`/projeler/${product.slug}`} light>Detay</ArrowLink>
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    </section>
);

/* -------------------------------- Süreç --------------------------------- */
const STEPS = [
    { n: "01", title: "Dinleme", text: "Ücretsiz tanışma görüşmesinde ürünü, hedef kitleyi ve kısıtları konuşuruz." },
    { n: "02", title: "Yol haritası", text: "Kapsam, teknoloji seçimi, takvim ve bütçe; yazılı ve net." },
    { n: "03", title: "Geliştirme", text: "Haftalık demo ve sade dille ilerleme notları. Sürpriz yok." },
    { n: "04", title: "Yayın", text: "Mağaza listeleri, test dağıtımı, canlıya alma ve ilk hafta takibi." },
    { n: "05", title: "Büyüme", text: "Analitik, abonelik ve mağaza optimizasyonu ile ürünü büyütme." },
];

const Process = () => (
    <section id="surec" className="container-site scroll-mt-[110px] pt-[96px] md:pt-[140px]" aria-labelledby="process-title">
        <Reveal><p className="t-eyebrow">Nasıl çalışırım</p></Reveal>
        <h2 id="process-title" className="t-h2 mt-4 max-w-[760px]">
            <SplitReveal text="Lansmana ve sonrasına kadar beş adım" />
        </h2>
        <div className="mt-12 md:mt-16">
            {STEPS.map((step, index) => (
                <Reveal key={step.n} delay={index * 0.05} className="grid grid-cols-1 gap-3 border-t border-ink-100 py-8 md:grid-cols-12 md:gap-8">
                    <span className="t-eyebrow md:col-span-2">{step.n}</span>
                    <h3 className="t-h3 md:col-span-4">{step.title}</h3>
                    <p className="t-body text-ink-600 md:col-span-6">{step.text}</p>
                </Reveal>
            ))}
            <div className="border-t border-ink-100" />
        </div>
        <Reveal delay={0.1} className="mt-10">
            <a href={CONTACT_MAILTO} className="btn-pill">Teklif alın</a>
        </Reveal>
    </section>
);

/* ------------------------------ Manifesto ------------------------------- */

const Statement = () => (
    <section className="mt-[96px] bg-ink-50 py-[96px] md:mt-[140px] md:py-[140px]" aria-labelledby="statement-title">
        <div className="container-site">
            <h2 id="statement-title" className="t-h2 max-w-[760px]">
                <SplitReveal text="Fikirleri yayınlanan ürünlere dönüştürüyorum" />
            </h2>
            <Reveal delay={0.1} className="mt-8 max-w-[640px]">
                <p className="t-intro font-normal text-ink">
                    Üniversiteler, girişimler ve kendi kullanıcılarım için yedi yıl. Kısa döngüler, haftalık demolar ve sade dille ilerleme notları; Ankara'dan neredeyseniz oraya.
                </p>
            </Reveal>
            <Reveal delay={0.2} className="mt-10">
                <ArrowLink to="/hakkimda">Beni tanıyın</ArrowLink>
            </Reveal>
        </div>
    </section>
);

/* --------------------------------- SSS ---------------------------------- */
const FAQ = [
    { q: "Hangi tür projeler yapıyorsunuz?", a: "Önce mobil: iOS ve Android için React Native ve Flutter uygulamaları. Çevresinde Node.js arka ucu, tanıtım sitesi ya da yönetim paneli; ürün gerektirdiğinde Unity ile oyun." },
    { q: "Girişimlerle ve tek kurucularla çalışıyor musunuz?", a: "Evet. Müşteri işlerimin çoğu bir fikir ve bir tarihle başladı. MVP kapsamını birlikte belirler, geliştirir, mağazalara çıkarır ve gerçek kullanım verisiyle iyileştiririz." },
    { q: "Fiyatlandırma nasıl?", a: "Kapsamı net işlerde sabit fiyat, süregelen ürün çalışmasında zaman ve malzeme, danışman desteği isteyen ekipler için aylık anlaşma. Her iş kısa bir keşif görüşmesiyle başlar." },
    { q: "Mevcut bir uygulamayı devralabilir misiniz?", a: "Sık yaptığım bir şey: kod ve mağaza durumunu inceler, öncelikli bir plan çıkarır, sonra işe koyulurum. Hem müşteri hesaplarında hem kendi hesaplarımda yayın yaptım." },
    { q: "Uzaktan ve farklı saat dilimlerinde çalışıyor musunuz?", a: "Ankara'dayım; Avrupa, Orta Doğu ve ABD'deki ekiplerle çalışıyorum. Haftalık demo, asenkron notlar ve baştan belirlenmiş ortak saatler." },
    { q: "Bir MVP ne kadar sürer?", a: "Kapsama göre 4 ila 8 hafta içinde test edilebilir bir sürüm, 2 ila 4 ay içinde mağazada yayın hedeflerim. Net takvimi keşif görüşmesinden sonra veririm." },
];

const Faq = () => (
    <section className="bg-ink-900 py-[96px] text-white md:py-[140px]" aria-labelledby="faq-title">
        <div className="container-site">
            <h2 id="faq-title" className="t-h2 text-white">
                <SplitReveal text="Sık sorulan sorular" />
            </h2>
            <Reveal delay={0.1} className="mt-10 md:mt-16">
                {FAQ.map((item) => (
                    <AccordionItem key={item.q} title={item.q} dark titleClassName="text-[20px] md:text-[26px] font-medium leading-snug">
                        {item.a}
                    </AccordionItem>
                ))}
            </Reveal>
        </div>
    </section>
);

/* ----------------------------- Kapanış CTA ------------------------------ */
const Cta = () => (
    <section className="container-site py-[96px] md:py-[140px]" aria-labelledby="cta-title">
        <Reveal><p className="t-eyebrow">Bir projeniz mi var?</p></Reveal>
        <h2 id="cta-title" className="t-display mt-4 max-w-[900px]">
            <SplitReveal text="Projenizi konuşalım." />
        </h2>
        <Reveal delay={0.2} className="mt-10 flex flex-wrap items-center gap-x-10 gap-y-4">
            <a href={CONTACT_MAILTO} className="btn-pill">Ücretsiz tanışma görüşmesi</a>
            <ArrowLink href="https://www.linkedin.com/in/abd%C3%BC-samed-akg%C3%BCl-383906173/">LinkedIn'den yazın</ArrowLink>
        </Reveal>
    </section>
);

function Home() {
    return (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, ease: EASE_OUT }} className="bg-white">
            <Hero />
            <Brands />
            <Intro />
            <Services />
            <Featured />
            <Products />
            <Process />
            <Statement />
            <Faq />
            <Cta />
        </motion.div>
    );
}

export default Home;
